import { trackAuthNavigation } from '@app/authNavigation';
import { defineAsyncComponent } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';

import type { UserRoleCatalogRouteGroup } from '@krafta/shared-types';

import { useAuthStore } from '@app/stores/auth';
import { useAttendanceLockStore } from '@features/attendance/attendanceLockStore';
import {
  resolveAuthedHomeRoute,
  resolveAuthedHomeRouteWithCatalog
} from '@shared/routing/authHomeRoute';
import { ensureProcessRollout } from '@features/processes/composables/useProcessPermissions';
import {
  getUserRoleCatalogRolesByRouteGroup,
  loadUserRoleCatalog
} from '@shared/reference-catalogs/useUserRoleCatalog';

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean;
    roleCatalogRouteGroup?: UserRoleCatalogRouteGroup;
    processPermission?: string;
    layoutVariant?: 'standard' | 'workspace' | 'fullscreen' | 'kiosk';
    terminal?: boolean;
    helpKey?: string;
    helpDisabled?: { reason: string };
  }
}

const DashboardPage = () => import('@pages/DashboardPage.vue');
const CalendarPage = () => import('@features/calendar/pages/CalendarPage.vue');
const HelpCenterPage = () => import('@features/help/pages/HelpCenterPage.vue');
const LoginPage = () => import('@pages/LoginPage.vue');
const AttendanceScanPage = () => import('@pages/attendance/AttendanceScanWorkspace.vue');
const OperatorScannerPage = () => import('@pages/operator/OperatorScannerWorkspace.vue');
const OperatorShiftPage = () => import('@pages/operator/OperatorShiftWorkspace.vue');
const OperatorStagesPage = () => import('@pages/operator/OperatorStagesWorkspace.vue');
const TaskAssignmentsPage = () => import('@pages/task-assignments/TaskAssignmentsWorkspace.vue');
const PlanningPage = () => import('@pages/planning/PlanningWorkspace.vue');
const StationManagementPage = () => import('@pages/stations/StationManagementWorkspace.vue');
const InventoryPage = () => import('@pages/inventory/InventoryWorkspace.vue');
const InventoryBomsPage = () => import('@pages/inventory-boms/InventoryBomsWorkspace.vue');
const ManufacturingWorkspacePage = () => import('@pages/manufacturing/ManufacturingWorkspace.vue');
const ProductionRouteLaunchPage = () => import('@pages/ProductionRouteLaunchPage.vue');
const ProcurementPage = () => import('@pages/procurement/ProcurementWorkspace.vue');
const SalesOrdersPage = () => import('@pages/sales-orders/SalesOrdersWorkspace.vue');
const CounterpartiesPage = () => import('@pages/counterparties/CounterpartiesWorkspace.vue');

const UsersListPage = () => import('@features/users/pages/UsersListPage.vue');
const UserDetailsPage = () => import('@features/users/pages/UserDetailsPage.vue');
const WorkplaceShiftMonitoringPage = () => import('@features/attendance/pages/WorkplaceShiftMonitoringPage.vue');
const workplaceShiftMonitoringView = defineAsyncComponent(() => import('@features/attendance/components/AttendanceWorkplaceShiftMonitoringView.vue'));

const ProcessHomeView = () => import('@features/processes/views/HomeView.vue');
const ProcessAnalyticsView = () => import('@features/processes/views/AnalyticsView.vue');
const ProcessAuditView = () => import('@features/processes/views/AuditView.vue');
const GraphExplorerPage = () => import('@features/process-graph/views/GraphExplorerPage.vue');
const ProcessEditor = () => import('@features/processes/components/ProcessEditor.vue');
const TaskList = () => import('@features/processes/views/worker/TaskList.vue');
const TaskExecute = () => import('@features/processes/views/worker/TaskExecute.vue');
const InjectionMoldingTerminalPage = () => import('@pages/InjectionMoldingTerminalPage.vue');
const InjectionMoldingQualityPage = () => import('@pages/InjectionMoldingQualityPage.vue');
const InjectionMoldingWarehousePage = () => import('@pages/InjectionMoldingWarehousePage.vue');
const InjectionMoldingTransferPage = () => import('@pages/InjectionMoldingTransferPage.vue');

export { resolveAuthedHomeRoute, resolveAuthedHomeRouteWithCatalog };

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'root',
      component: DashboardPage,
      meta: { helpDisabled: { reason: 'Перенаправление на стартовый раздел пользователя.' } }
    },
    { path: '/dashboard', name: 'dashboard', component: DashboardPage, meta: { helpKey: 'dashboard.overview' } },
    {
      path: '/calendar',
      name: 'calendar',
      component: CalendarPage,
      meta: { layoutVariant: 'workspace' as const, helpKey: 'calendar.overview' }
    },
    {
      path: '/help',
      name: 'help-center',
      component: HelpCenterPage,
      meta: { layoutVariant: 'fullscreen' as const, helpKey: 'help.center' }
    },
    {
      path: '/help/:key',
      name: 'help-page',
      component: HelpCenterPage,
      meta: { layoutVariant: 'fullscreen' as const, helpKey: 'help.center' }
    },
    { path: '/workplaces/assembly', name: 'assembly-terminal', component: () => import('../../pages/AssemblyTerminalPage.vue'),
      meta: { public: true, terminal: true, layoutVariant: 'fullscreen' as const } },
    { path: '/workplaces/mechanical', name: 'mechanical-terminal', component: () => import('@pages/MechanicalTerminalPage.vue'),
      meta: { public: true, terminal: true, layoutVariant: 'fullscreen' as const } },
    { path: '/login', name: 'login', component: LoginPage, meta: { public: true } },
    { path: '/login/qr', name: 'login-qr', component: LoginPage, meta: { public: true } },
    {
      path: '/workplaces/injection-molding',
      name: 'injection-molding-terminal',
      component: InjectionMoldingTerminalPage,
      meta: { public: true, terminal: true, layoutVariant: 'fullscreen' as const }
    },
    { path: '/workplaces/mechanical-quality', name: 'mechanical-quality', component: () => import('@pages/MechanicalQualityPage.vue'),
      meta: { roleCatalogRouteGroup: 'quality-workspace', layoutVariant: 'fullscreen' as const, helpDisabled: { reason: 'Рабочее место ОТК мехцеха.' } } },
    { path: '/workplaces/mechanical-warehouse', name: 'mechanical-warehouse', component: () => import('@pages/MechanicalWarehousePage.vue'),
      meta: { roleCatalogRouteGroup: 'inventory-write', layoutVariant: 'fullscreen' as const, helpDisabled: { reason: 'Приёмка ящиков мехцеха.' } } },
    {
      path: '/workplaces/quality-control',
      name: 'injection-molding-quality',
      component: InjectionMoldingQualityPage,
      meta: {
        roleCatalogRouteGroup: 'quality-workspace',
        layoutVariant: 'fullscreen' as const,
        helpDisabled: { reason: 'Специализированное рабочее место ОТК.' }
      }
    },
    {
      path: '/workplaces/injection-molding-warehouse',
      name: 'injection-molding-warehouse',
      component: InjectionMoldingWarehousePage,
      meta: {
        roleCatalogRouteGroup: 'inventory-write',
        layoutVariant: 'fullscreen' as const,
        helpDisabled: { reason: 'Специализированное рабочее место складской приёмки.' }
      }
    },
    {
      path: '/workplaces/injection-molding-transfer',
      name: 'injection-molding-transfer',
      component: InjectionMoldingTransferPage,
      meta: {
        roleCatalogRouteGroup: 'inventory-write',
        layoutVariant: 'fullscreen' as const,
        helpDisabled: { reason: 'Специализированное рабочее место выдачи в сборку.' }
      }
    },
    {
      path: '/attendance/scan',
      name: 'attendance-scan',
      component: AttendanceScanPage,
      meta: { layoutVariant: 'fullscreen' as const, helpKey: 'operator.shift' }
    },
    {
      path: '/operator/scanner',
      name: 'operator-scanner',
      component: OperatorScannerPage,
      meta: { roleCatalogRouteGroup: 'operator-workspace', helpKey: 'operator.scanner' }
    },
    {
      path: '/operator/shift',
      name: 'operator-shift',
      component: OperatorShiftPage,
      meta: { roleCatalogRouteGroup: 'operator-workspace', helpKey: 'operator.shift' }
    },
    {
      path: '/operator/stages',
      name: 'operator-stages',
      component: OperatorStagesPage,
      meta: { roleCatalogRouteGroup: 'operator-workspace', helpKey: 'operator.stages' }
    },
    
    {
      path: '/workplace-shifts',
      name: 'workplace-shift-monitoring',
      component: WorkplaceShiftMonitoringPage,
      meta: { roleCatalogRouteGroup: 'workplace-shift-monitoring', helpDisabled: { reason: 'Смены рабочих мест.' } }
    },
    {
      path: '/users',
      meta: { roleCatalogRouteGroup: 'user-management', helpKey: 'users.list' },
      children: [
        {
          path: '',
          name: 'users-list',
          component: UsersListPage,
          props: { shiftMonitoringView: workplaceShiftMonitoringView },
          meta: { helpKey: 'users.list' }
        },
        {
          path: ':id',
          name: 'user-details',
          component: UserDetailsPage,
          props: true,
          meta: { helpKey: 'users.details' }
        }
      ]
    },

    {
      path: '/counterparties',
      name: 'counterparties-list',
      component: CounterpartiesPage,
      meta: { roleCatalogRouteGroup: 'counterparty-workspace', helpKey: 'counterparties.list' }
    },
    {
      path: '/counterparties/:id',
      name: 'counterparties-details',
      component: CounterpartiesPage,
      meta: { roleCatalogRouteGroup: 'counterparty-workspace', helpKey: 'counterparties.list' },
      props: true
    },

    {
      path: '/planning',
      name: 'planning',
      component: PlanningPage,
      meta: {
        roleCatalogRouteGroup: 'planning-workspace',
        layoutVariant: 'workspace' as const,
        helpKey: 'planning.overview'
      }
    },
    {
      path: '/assignments',
      name: 'assignments',
      component: TaskAssignmentsPage,
      meta: {
        roleCatalogRouteGroup: 'production-control-workspace',
        layoutVariant: 'workspace' as const,
        helpKey: 'tasks.assignments'
      }
    },
    { path: '/stations', redirect: '/workplaces', meta: { helpDisabled: { reason: 'Legacy redirect to workplaces.' } } },
    {
      path: '/workplaces/assembly/shifts',
      name: 'assembly-shift-management',
      component: () => import('@pages/stations/AssemblyShiftManagementPage.vue'),
      meta: { roleCatalogRouteGroup: 'workplace-shift-management', helpDisabled: { reason: 'Administrative assembly shift closure.' } }
    },
    {
      path: '/workplaces/mechanical-management',
      name: 'mechanical-management',
      component: () => import('@pages/stations/MechanicalManagementPage.vue'),
      meta: { roleCatalogRouteGroup: 'mechanical-assignment', helpDisabled: { reason: 'Назначение и подготовка станков мехцеха.' } }
    },
    {
      path: '/workplaces/injection-molding-assignments',
      name: 'injection-molding-assignments',
      component: () => import('@pages/stations/InjectionMoldingAssignmentPage.vue'),
      meta: { roleCatalogRouteGroup: 'injection-molding-assignment', helpDisabled: { reason: 'Casting task allocation by master.' } }
    },
    {
      path: '/workplaces/injection-molding-tooling',
      name: 'injection-molding-tooling',
      component: () => import('@pages/stations/InjectionMoldingToolingPage.vue'),
      meta: { roleCatalogRouteGroup: 'workplace-management', helpDisabled: { reason: 'Physical mold registry.' } }
    },
    {
      path: '/workplaces',
      name: 'workplaces',
      component: StationManagementPage,
      meta: { roleCatalogRouteGroup: 'workplace-management', helpKey: 'stations.management' }
    },
    {
      path: '/inventory',
      name: 'inventory',
      component: InventoryPage,
      meta: { roleCatalogRouteGroup: 'inventory-workspace', helpKey: 'inventory.overview' }
    },
    {
      path: '/manufacturing',
      name: 'manufacturing-workspace',
      component: ManufacturingWorkspacePage,
      meta: { roleCatalogRouteGroup: 'manufacturing-workspace', helpKey: 'manufacturing.workspace' }
    },
    {
      path: '/production/route-launches',
      name: 'production-route-launch-create',
      component: ProductionRouteLaunchPage,
      meta: {
        roleCatalogRouteGroup: 'production-route-launch-create',
        layoutVariant: 'workspace' as const,
        helpKey: 'manufacturing.routeLaunch'
      }
    },
    {
      path: '/production/route-launches/:batchId',
      name: 'production-route-launch',
      component: ProductionRouteLaunchPage,
      meta: {
        roleCatalogRouteGroup: 'production-route-launch-view',
        layoutVariant: 'workspace' as const,
        helpKey: 'manufacturing.routeLaunch'
      }
    },
    {
      path: '/inventory/boms',
      name: 'inventory-boms',
      component: InventoryBomsPage,
      meta: { roleCatalogRouteGroup: 'inventory-bom-management', helpKey: 'inventory.boms' }
    },
    {
      path: '/inventory/boms/:bomId',
      name: 'inventory-bom-detail',
      component: InventoryBomsPage,
      meta: { roleCatalogRouteGroup: 'inventory-bom-management', helpKey: 'inventory.boms' }
    },
    {
      path: '/inventory/boms/:bomId/preview',
      name: 'inventory-bom-preview',
      component: InventoryBomsPage,
      meta: { roleCatalogRouteGroup: 'inventory-bom-management', helpKey: 'inventory.boms' }
    },
    {
      path: '/procurement',
      name: 'procurement',
      component: ProcurementPage,
      meta: { roleCatalogRouteGroup: 'procurement-workspace', helpKey: 'procurement.overview' }
    },
    {
      path: '/sales-orders',
      name: 'sales-orders',
      component: SalesOrdersPage,
      meta: { roleCatalogRouteGroup: 'sales-order-workspace', helpKey: 'sales.orders' }
    },
    {
      path: '/sales-orders/:orderId/edit',
      name: 'sales-order-edit',
      component: SalesOrdersPage,
      meta: { roleCatalogRouteGroup: 'sales-order-workspace', helpKey: 'sales.orders' }
    },
    { path: '/workflows', redirect: '/workflows-next', meta: { helpDisabled: { reason: 'Legacy redirect to process constructor.' } } },
    {
      path: '/graph',
      name: 'process-graph-explorer',
      component: GraphExplorerPage,
      meta: { processPermission: 'process.viewAnalytics', layoutVariant: 'workspace' as const, helpKey: 'process.graph' }
    },

    // Workflows Next (Rete-based Process Engine)
    {
      path: '/workflows-next',
      name: 'workflows-next-home',
      component: ProcessHomeView,
      meta: { processPermission: 'process.view', helpKey: 'process.builder' }
    },
    {
      path: '/workflows-next/editor/:id',
      name: 'workflows-next-editor',
      component: ProcessEditor,
      meta: { processPermission: 'process.editDraft', layoutVariant: 'fullscreen' as const, helpKey: 'process.editor' }
    },
    {
      path: '/workflows-next/graph',
      name: 'krafta-graph-explorer',
      component: GraphExplorerPage,
      meta: { processPermission: 'process.viewAnalytics', layoutVariant: 'workspace' as const, helpKey: 'process.graph' }
    },
    {
      path: '/workflows-next/analytics',
      name: 'workflows-next-analytics',
      component: ProcessAnalyticsView,
      meta: { processPermission: 'process.viewAnalytics', layoutVariant: 'workspace' as const, helpKey: 'process.analytics' }
    },
    {
      path: '/workflows-next/audit',
      name: 'workflows-next-audit',
      component: ProcessAuditView,
      meta: { processPermission: 'process.viewAudit', layoutVariant: 'workspace' as const, helpKey: 'process.audit' }
    },
    {
      path: '/workflows-next/tasks',
      name: 'workflows-next-tasks',
      component: TaskList,
      meta: { processPermission: 'process.executeTask', layoutVariant: 'workspace' as const, helpKey: 'process.center' }
    },
    {
      path: '/workflows-next/tasks/:id/kiosk',
      name: 'workflows-next-task-kiosk',
      component: TaskExecute,
      meta: { processPermission: 'process.executeTask', layoutVariant: 'kiosk' as const, helpKey: 'process.task' }
    },
    {
      path: '/workflows-next/tasks/:id',
      name: 'workflows-next-task',
      component: TaskExecute,
      meta: { processPermission: 'process.executeTask', layoutVariant: 'workspace' as const, helpKey: 'process.task' }
    }
  ]
});

trackAuthNavigation(router);

const ATTENDANCE_LOCK_ALLOWED_ROUTES = new Set(['dashboard', 'attendance-scan', 'injection-molding-assignments', 'mechanical-management']);

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const attendanceLock = useAttendanceLockStore();
  // Presentation only: the route catalog and MES still authorize every master action.
  attendanceLock.setRouteExempt(['injection-molding-assignments', 'mechanical-management'].includes(String(to.name ?? '')));

  if (to.name === 'root') {
    return auth.isAuthenticated ? resolveAuthedHomeRouteWithCatalog(auth) : { name: 'login' };
  }

  if (!auth.isAuthenticated && !to.meta.public) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  if (auth.isAuthenticated && (to.name === 'login' || to.name === 'login-qr')) {
    return resolveAuthedHomeRouteWithCatalog(auth);
  }

  const requiredRoleCatalogGroup = to.meta?.roleCatalogRouteGroup;
  if (requiredRoleCatalogGroup) {
    await loadUserRoleCatalog(auth.authHeaders);
    const requiredCatalogRoles = getUserRoleCatalogRolesByRouteGroup(requiredRoleCatalogGroup);
    if (!requiredCatalogRoles.length || !auth.hasAnyRole(requiredCatalogRoles)) {
      return resolveAuthedHomeRoute(auth);
    }
  }

  const requiredProcessPermission = to.meta?.processPermission;
  if (requiredProcessPermission) {
    const processRollout = await ensureProcessRollout(auth.token, auth.user?.roles);
    if (!processRollout?.currentUser.permissions[requiredProcessPermission]) {
      return resolveAuthedHomeRouteWithCatalog(auth);
    }
  }

  if (auth.isAuthenticated && !to.meta.public) {
    await attendanceLock.ensureReady();
    const isAllowedWhileLocked = ATTENDANCE_LOCK_ALLOWED_ROUTES.has(String(to.name ?? ''));
    if (attendanceLock.isLocked && !isAllowedWhileLocked) {
      return { name: 'dashboard' };
    }
  }

  return true;
});

export default router;
